import { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import {
  DownloadOutlined as DownloadIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';

import api from '../../services/api';
import {
  downloadBlob,
  openBlobInNewTab,
  resolveBlobErrorMessage,
} from '../../utils/blobFileActions';
import { extractErrorMessage } from '../../utils/errorUtils';
import {
  FINANCE_EMAIL_ATTACHMENT_API,
  useGetFinanceEmailAttachmentsQuery,
  type FinanceEmailAttachment,
} from '../grid/api/financeEmailApi';
import { isFinanceEmailHistoryId } from '../grid/utils/financeEmailHistoryId';
import { DetailFormFieldShell } from './DetailFormFieldShell';
import type { ResponsiveWidth } from './types';

type FinanceEmailAttachmentsFieldProps = {
  historyId: string;
  label: string;
  hideCaption?: boolean;
  captionsWidth?: ResponsiveWidth;
};

export const FinanceEmailAttachmentsField = ({
  historyId,
  label,
  hideCaption,
  captionsWidth,
}: FinanceEmailAttachmentsFieldProps) => {
  const isValidHistoryId = isFinanceEmailHistoryId(historyId);
  const {
    data,
    isLoading,
    isError,
    error: queryError,
  } = useGetFinanceEmailAttachmentsQuery(historyId, {
    skip: !isValidHistoryId,
  });

  const [actionError, setActionError] = useState<string | null>(null);
  const [actionKey, setActionKey] = useState<string | null>(null);

  const attachments = data?.attachments ?? [];
  const listError = !isValidHistoryId
    ? `Invalid email id "${historyId}". Expected format like o9 or i5.`
    : isError
      ? extractErrorMessage(queryError, 'Failed to load attachments.')
      : null;
  const error = actionError || listError;

  const handleAction = async (attachment: FinanceEmailAttachment, mode: 'view' | 'download') => {
    setActionError(null);
    setActionKey(`${mode}:${attachment.name}`);

    try {
      const endpoint =
        mode === 'view'
          ? `${FINANCE_EMAIL_ATTACHMENT_API}/View`
          : `${FINANCE_EMAIL_ATTACHMENT_API}/Download`;

      const response = await api.get(endpoint, {
        params: { historyId, fileName: attachment.name },
        responseType: 'blob',
      });

      const blob = response.data as Blob;
      if (mode === 'view') {
        openBlobInNewTab(blob);
      } else {
        downloadBlob(blob, attachment.name);
      }
    } catch (actionErr: unknown) {
      setActionError(
        await resolveBlobErrorMessage(
          actionErr,
          mode === 'view' ? 'Failed to open attachment.' : 'Failed to download attachment.'
        )
      );
    } finally {
      setActionKey(null);
    }
  };

  return (
    <DetailFormFieldShell label={label} hideCaption captionsWidth={captionsWidth}>
      <Box sx={{ width: '100%' }}>
        {!hideCaption && (
          <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
            {label}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading attachments…
            </Typography>
          </Stack>
        ) : attachments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No attachments
          </Typography>
        ) : (
          <Stack spacing={1}>
            {attachments.map((attachment) => {
              const isBusy = actionKey?.endsWith(`:${attachment.name}`);
              return (
                <Stack
                  key={attachment.name}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                  sx={{
                    border: '1px solid',
                    borderColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.23)'
                        : 'rgba(0, 0, 0, 0.23)',
                    borderRadius: (theme) => `${theme.shape.borderRadius}px`,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap title={attachment.name}>
                      {attachment.name}
                    </Typography>
                    {!attachment.available && (
                      <Typography variant="caption" color="text.secondary">
                        File is not available
                      </Typography>
                    )}
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    <Tooltip title={attachment.canView ? 'View PDF' : 'Only PDF can be viewed'}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={
                            actionKey === `view:${attachment.name}` ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <ViewIcon fontSize="small" />
                            )
                          }
                          disabled={!attachment.canView || Boolean(isBusy)}
                          onClick={() => void handleAction(attachment, 'view')}
                          sx={{ textTransform: 'none' }}
                        >
                          View
                        </Button>
                      </span>
                    </Tooltip>

                    <Tooltip title="Download">
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={
                            actionKey === `download:${attachment.name}` ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <DownloadIcon fontSize="small" />
                            )
                          }
                          disabled={!attachment.available || Boolean(isBusy)}
                          onClick={() => void handleAction(attachment, 'download')}
                          sx={{ textTransform: 'none' }}
                        >
                          Download
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Box>
    </DetailFormFieldShell>
  );
};
